import React, { useEffect, useState } from 'react';
import { Box } from '@withjoy/joykit';
import type { ImageLayerData, LayerLayoutData, Size } from './Layer.types';
import type { PageEditor } from './Page';
import { loadImageDimensions } from '@shared/utils/loadImageDimensions';
import { EditableCroppedImageLayer, CroppedImageLayer } from './CroppedImageLayer';
import { layerLayoutToCss } from '@apps/card/utils';
import type { Rendition } from '@apps/card/utils/mediaService';
import { applyRenditionIfMediaServiceUrl } from '@apps/card/utils/mediaService';
import { absolutePhotoUrl, previewUrls, SCALE_MAX } from './ImageLayer.utils';

export { absolutePhotoUrl, previewUrls, SCALE_MAX };
export type { Size };

function backgroundOrMaskProps({ url, mask, rendition, layout }: { url: string; mask?: { color: string }; rendition?: Rendition; layout: LayerLayoutData }): React.CSSProperties {
  const prefix = mask ? 'mask' : 'background';

  // CSS variable references (e.g. "var(--image1)") are already valid background-image values —
  // the resolved value is a url() set by an ancestor element's inline style.
  const backgroundImage = url.startsWith('var(') ? url : `url(${applyRenditionIfMediaServiceUrl(url, rendition, layout)})`;

  return {
    // Images sizes served via Media Service (withjoy.com/media) vs directly from Azure Blob:
    //                          Azure     Media Service
    // Examples invitations:    Blob      Default  Medium
    //   cherie                 12.7MB    1.6MB    408KB
    //   bohemian_eucalyptus     3.1MB    556KB    119KB
    [prefix + 'Image']: backgroundImage,
    [prefix + 'Size']: 'cover',
    [prefix + 'Position']: 'center',
    [prefix + 'Repeat']: 'no-repeat',
    // Unprefixed `mask-*` is Chrome 120+ / Safari 15.4+; the `-webkit-` form goes back years and is
    // still what older engines read. Without it a masked layer falls back to no mask at all, which
    // paints the fill color as a flat rectangle over the glyph.
    ...(mask
      ? {
          backgroundColor: mask.color,
          WebkitMaskImage: backgroundImage,
          WebkitMaskSize: 'cover',
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
        }
      : {}),
  };
}

// Could merge with CroppedImageLayer, but would need to wait for the image natural w/h to load before render, which is not ideal.
export const ImageLayer = (props: { layerIndex: number; layer: ImageLayerData; editor?: PageEditor; mask?: { color: string }; rendition?: Rendition }) => {
  const { layer, editor, layerIndex, mask: maskProp, rendition } = props;
  const isActive = editor?.isActive(layerIndex) ?? false;

  // A filled glyph masks itself to one color (see `utils/frontColorsOnBack`). The page-wide
  // `mask` prop still wins where it is set, so the website-designer monogram keeps its behaviour.
  const mask = maskProp ?? (layer.imageData.fill ? { color: layer.imageData.fill } : undefined);

  const url = previewUrls.get(layer.imageData.src) ?? absolutePhotoUrl(layer.imageData.src);

  // Design decision: a colorway image (`var(--N)` src) is never cropped, so it always renders on the
  // background-image path below, even when editable and active. With no crop, the cropped <img>
  // path would only reproduce `background-size: cover` centered (`getScalePanToFit`), and that path
  // can't read a CSS var: <img src> and the dimension probe need a concrete URL. Skipping
  // `EditableCroppedImageLayer` also leaves `scaleRef` unset, which hides the zoom slider, so a crop
  // can never be written onto a var src. Uploading a photo replaces the var with a real src, which
  // then takes the cropped path and wins over every later colorway change.
  const isColorwayImage = url.startsWith('var(');
  const isCroppable = layer.editable && !isColorwayImage;

  const [photoSize, setPhotoSize] = useState<Size>(); // Only used if isCroppable
  useEffect(() => {
    if (!isCroppable) return;
    // Drop out-of-order loads so a slow earlier photo can't overwrite the current one's size. The
    // previous size is kept until the new one loads, so a swap doesn't flash the uncropped fallback.
    let isCancelled = false;
    void (async () => {
      const size = await loadImageDimensions(url);
      if (!isCancelled) setPhotoSize(size);
    })();
    return () => {
      isCancelled = true;
    };
  }, [isCroppable, url]);

  if (isCroppable && photoSize) {
    return isActive && editor ? (
      <EditableCroppedImageLayer
        {...props}
        editor={editor}
        photoSize={photoSize}
      />
    ) : (
      <CroppedImageLayer
        layer={layer}
        photoSize={photoSize}
        rendition={rendition}
      />
    );
  }

  // Also the fallback while the probe is unresolved — in flight, or forever, since
  // `loadImageDimensions` never settles on error. `cover` needs no dimensions.
  return (
    <>
      <Box
        style={{
          ...layerLayoutToCss(layer.layout),
          overflow: 'hidden', // Safari not responding to 'clip'
          ...backgroundOrMaskProps({ url, mask, rendition, layout: layer.layout }),
        }}
      />
    </>
  );
};
